// Date of birth checks for the host forms. Mirrors functions/dateOfBirth.js;
// the server is the final word. The register form once saved the text
// "Invalid Date" from a half-typed picker, so check the value, not just presence.

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MIN_YEAR = 1900;
// Ages are counted on Melbourne's calendar day, like the server, whatever the browser's timezone.
const TIME_ZONE = 'Australia/Melbourne';

export const DOB_MESSAGES = Object.freeze({
  MISSING: 'Enter your date of birth.',
  INVALID: 'Enter your full date of birth as day, month and year.',
  UNDER_AGE: 'You must be 18 or over to register.',
  TOO_OLD: 'Check the year in your date of birth.'
});

export function parseIsoDay(raw) {
  const m = ISO_DAY.exec(String(raw ?? '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  return { year, month, day };
}

function todayParts(now = new Date(), timeZone = TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

function ageOn(dob, today) {
  let age = today.year - dob.year;
  if (today.month < dob.month || (today.month === dob.month && today.day < dob.day)) age -= 1;
  return age;
}

// -> { ok: true, value } or { ok: false, code, message }
export function checkDateOfBirth(raw, { now = new Date(), timeZone = TIME_ZONE, minAge = 18, maxAge = 100 } = {}) {
  const text = String(raw ?? '').trim();
  if (!text) return { ok: false, code: 'MISSING', message: DOB_MESSAGES.MISSING };
  const dob = parseIsoDay(text);
  if (!dob || dob.year < MIN_YEAR) return { ok: false, code: 'INVALID', message: DOB_MESSAGES.INVALID };
  const age = ageOn(dob, todayParts(now, timeZone));
  if (age < minAge) return { ok: false, code: 'UNDER_AGE', message: DOB_MESSAGES.UNDER_AGE };
  if (age > maxAge) return { ok: false, code: 'TOO_OLD', message: DOB_MESSAGES.TOO_OLD };
  return { ok: true, value: text };
}

// A date picker value (dayjs or null) as the stored 'YYYY-MM-DD' text.
// A half-typed value is a dayjs whose isValid() is false: that becomes 'invalid', never "Invalid Date".
export function pickerValueToIso(value) {
  if (!value) return '';
  if (typeof value.isValid === 'function' && !value.isValid()) return 'invalid';
  return typeof value.format === 'function' ? value.format('YYYY-MM-DD') : String(value);
}

// A stored value safe to show again, or '' (drops "Invalid Date" and other junk).
export function cleanStoredDateOfBirth(raw) {
  const text = String(raw ?? '').trim();
  const dob = parseIsoDay(text);
  return dob && dob.year >= MIN_YEAR ? text : '';
}

const pad2 = (n) => String(n).padStart(2, '0');
const isoOf = (y, m, d) => `${String(y).padStart(4, '0')}-${pad2(m)}-${pad2(d)}`;
const lastDayOf = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

// Picker limits as 'YYYY-MM-DD', matching checkDateOfBirth exactly (Melbourne day, ages minAge to
// maxAge inclusive). max = the same day minAge years ago (28 Feb when that year has no 29 Feb);
// min = the day after the same day (maxAge + 1) years ago.
export function dateOfBirthBounds({ now = new Date(), timeZone = TIME_ZONE, minAge = 18, maxAge = 100 } = {}) {
  const t = todayParts(now, timeZone);
  const maxYear = t.year - minAge;
  const max = isoOf(maxYear, t.month, Math.min(t.day, lastDayOf(maxYear, t.month)));
  const tooOldYear = t.year - maxAge - 1;
  const after = new Date(Date.UTC(tooOldYear, t.month - 1, Math.min(t.day, lastDayOf(tooOldYear, t.month)) + 1));
  const min = isoOf(after.getUTCFullYear(), after.getUTCMonth() + 1, after.getUTCDate());
  return { min, max };
}

// A stored value the host should be asked to fix: present but not a real adult date
// ("Invalid Date", "14/07/2006", a year left on 2025). A missing value is not asked about.
export function needsDateOfBirthFix(stored, opts = {}) {
  if (String(stored ?? '').trim() === '') return false;
  return !checkDateOfBirth(stored, opts).ok;
}
